import { useState, Fragment } from 'react'
import { Search, Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TraySize } from '@/types/app'
import type { PublicMenuItem, SelectionEntry, TrayKey } from '@/hooks/usePublicCateringQuote'
import { selectionKey } from '@/hooks/usePublicCateringQuote'

interface Group {
  id: string
  name: string
  items: PublicMenuItem[]
}

interface Props {
  groups: Group[]
  selections: Map<string, SelectionEntry>
  // Tapping a size toggles that exact item+size combo on/off — independent of
  // any other size already selected for the same item, so e.g. one Large tray
  // and one Small tray of the same dish can both be active at once. `min` is
  // this size's minimum order (lbs, per-lb sizes only) — the starting
  // quantity when first tapped on, same idea as adding one tray at a time.
  onToggle: (itemId: string, traySize: TrayKey, min?: number | null) => void
  showCost: boolean
}

const SIZE_COLS: { size: TrayKey; label: string }[] = [
  { size: 'per_piece',    label: 'Pc'  },
  { size: 'half_tray',    label: 'Sm'  },
  { size: 'medium_tray',  label: 'Med' },
  { size: 'full_tray',    label: 'Lg'  },
  { size: 'per_lb',       label: 'Lb'  },
  { size: 'boxed_per_lb', label: 'Box' },
]

const SIZE_SHORT: Record<TrayKey, string> = {
  half_tray: 'Sm', medium_tray: 'Med', full_tray: 'Lg', per_piece: 'Pc',
  per_lb: 'Lb', boxed_per_lb: 'Box',
}

// Customer-facing menu browser — same compact matrix look as the internal
// staff picker (category rows, item rows, tap a size to add). Pricing is
// only shown when the "show pricing" checkbox is on — by default we keep it
// hidden and treat final pricing as something to confirm by phone.
//
// Tapping a size just toggles it on/off at quantity 1 — adjusting quantity
// beyond that happens back on the "Your order" review screen, since multiple
// sizes can be active per item and there isn't room for a qty stepper in
// every one of these narrow columns.
export function PublicCateringMenuMatrix({ groups, selections, onToggle, showCost }: Props) {
  const [search, setSearch] = useState('')

  const q = search.trim().toLowerCase()
  const filteredGroups = groups
    .map(g => ({
      ...g,
      items: q
        ? g.items.filter(i => i.name.toLowerCase().includes(q) || (i.regional_name ?? '').toLowerCase().includes(q))
        : g.items,
    }))
    .filter(g => g.items.length > 0)

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
      <div className="px-3 py-2.5 border-b border-stone-200">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            placeholder="Search item…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-stone-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse table-fixed">
          <colgroup>
            <col />
            {SIZE_COLS.map(c => <col key={c.size} className="w-11" />)}
          </colgroup>
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-stone-200">
              <th className="px-3 py-2 text-left font-semibold text-stone-500">Item</th>
              {SIZE_COLS.map(c => (
                <th key={c.size} className="px-0.5 py-2 text-center font-semibold text-stone-500">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredGroups.length === 0 && (
              <tr>
                <td colSpan={SIZE_COLS.length + 1} className="px-4 py-8 text-center text-stone-400">
                  No items match &quot;{search}&quot;
                </td>
              </tr>
            )}
            {filteredGroups.map(group => (
              <Fragment key={group.id}>
                <tr className="bg-stone-50">
                  <td colSpan={SIZE_COLS.length + 1} className="px-3 py-1 text-2xs font-bold uppercase tracking-widest text-stone-400 border-y border-stone-200">
                    {group.name}
                  </td>
                </tr>
                {group.items.map(item => {
                  const activeSizes = SIZE_COLS
                    .map(c => c.size)
                    .filter(size => (selections.get(selectionKey(item.id, size))?.quantity ?? 0) > 0)
                  return (
                    <tr key={item.id} className="border-b border-stone-100">
                      <td className="px-3 py-1.5">
                        <div className="min-w-0">
                          <p className="font-medium text-stone-800 truncate">{item.name}</p>
                          {item.regional_name && <p className="text-2xs text-stone-400 truncate">{item.regional_name}</p>}
                          {activeSizes.length > 0 && (
                            <p className="text-2xs text-success-dark font-medium mt-0.5">
                              Added: {activeSizes.map(size => {
                                const qty = selections.get(selectionKey(item.id, size))?.quantity ?? 0
                                return `${SIZE_SHORT[size]} ×${qty}`
                              }).join(', ')}
                            </p>
                          )}
                        </div>
                      </td>
                      {SIZE_COLS.map(c => {
                        const opt = item.tray_options[c.size]
                        const selected = (selections.get(selectionKey(item.id, c.size))?.quantity ?? 0) > 0
                        if (!opt.available) {
                          return <td key={c.size} className="px-0.5 py-1.5 text-center text-stone-200">—</td>
                        }
                        return (
                          <td key={c.size} className="px-0.5 py-1">
                            <button
                              type="button"
                              onClick={() => onToggle(item.id, c.size, opt.min)}
                              title={opt.serves ? `Serves ~${opt.serves}` : opt.min ? `Minimum ${opt.min} lb` : undefined}
                              className={cn(
                                'w-full rounded px-1 py-1.5 text-2xs font-semibold transition-colors flex items-center justify-center gap-0.5',
                                selected
                                  ? 'bg-success text-white hover:bg-success-dark'
                                  : 'bg-brand-50 text-brand-700 hover:bg-brand-100 active:bg-brand-200'
                              )}
                            >
                              {showCost && opt.price != null
                                ? <>{selected && <Check size={11} />}${opt.price.toFixed(0)}</>
                                : selected ? <Check size={12} /> : <Plus size={12} />}
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-2 border-t border-stone-100 text-center">
        <span className="text-2xs text-stone-400">Tap a size to add · tap again to remove · adjust quantity on the next screen</span>
      </div>
    </div>
  )
}

// Re-exported so callers building the TraySize import type from this component
// keep working unchanged.
export type { TraySize }
